import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, Alert,
  CircularProgress, Box, Typography, Chip
} from '@mui/material';

// Letter to the employee after a flexible working decision (approval or refusal).
// Pre-filled from the request record; the manager reviews and creates it.
//   <FlexLetterDialog open={!!id} requestId={id} onClose={() => setId(null)} />
export default function FlexLetterDialog({ open, requestId, onClose }) {
  const navigate = useNavigate();
  const [prefill, setPrefill] = useState(null);
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open || !requestId) return;
    let cancelled = false;
    setLoading(true); setError(''); setDone(null); setPrefill(null);
    api.get(`/letter-gen/prefill/flexible-working/${requestId}`)
      .then((res) => {
        if (cancelled) return;
        const { warnings, kind, employee_name, is_trial, ...editable } = res.data.prefill;
        setPrefill(res.data.prefill);
        setFields(editable);
      })
      .catch((e) => !cancelled && setError(e.response?.data?.error || 'Could not load this request.'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, requestId]);

  const set = (name) => (e) => setFields((f) => ({ ...f, [name]: e.target.value }));

  const handleGenerate = async () => {
    setSaving(true); setError('');
    try {
      const res = await api.post(`/letter-gen/generate/flexible-working/${requestId}`, fields);
      setDone(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not generate the letter.');
    } finally {
      setSaving(false);
    }
  };

  const text = (name, label, extra = {}) => (
    <TextField fullWidth size="small" label={label} value={fields[name] ?? ''} onChange={set(name)} {...extra} />
  );
  const date = (name, label) => text(name, label, { type: 'date', InputLabelProps: { shrink: true } });

  const approval = prefill?.kind === 'approval';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {approval ? 'Approval' : 'Refusal'} letter{prefill ? ` - ${prefill.employee_name}` : ''}
        {approval && prefill?.is_trial && <Chip size="small" color="info" label="Trial" sx={{ ml: 1 }} />}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ textAlign: 'center', p: 3 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {done ? (
          <Alert severity="success">
            {done.message}.{' '}
            <Button size="small" onClick={() => { onClose(); navigate('/documents'); }}>Open Document Vault</Button>
          </Alert>
        ) : prefill && (
          <>
            {prefill.warnings.map((w) => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The decision is recorded. Check the wording below, then create the letter; it is saved to
              your Document Vault for you to review and send.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>{date('meeting_date', 'Meeting date')}</Grid>
              {approval ? (
                <>
                  <Grid item xs={12} sm={4}>{date('effective_date', 'Effective from')}</Grid>
                  {prefill.is_trial && <Grid item xs={12} sm={4}>{date('trial_end_date', 'Trial ends')}</Grid>}
                  <Grid item xs={12}>{text('new_working_pattern', 'New working pattern', { multiline: true, minRows: 2 })}</Grid>
                  {prefill.is_trial && (
                    <>
                      <Grid item xs={12} sm={6}>{text('trial_duration', 'Trial length (e.g. 3 months)')}</Grid>
                      <Grid item xs={12} sm={6}>{date('review_date', 'Review date')}</Grid>
                      <Grid item xs={12}>{text('trial_criteria', 'What we will look at', { multiline: true, minRows: 2 })}</Grid>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Grid item xs={12} sm={4}>{date('requested_start_date', 'Requested start date')}</Grid>
                  <Grid item xs={12}>{text('requested_pattern', 'Requested working pattern', { multiline: true, minRows: 2 })}</Grid>
                  <Grid item xs={12}>{text('decline_reason', 'Reason for decline', { multiline: true, minRows: 4 })}</Grid>
                  <Grid item xs={12}>
                    {text('alternative_offered', 'Alternative offered (optional)', {
                      multiline: true, minRows: 2,
                      helperText: 'If left blank the letter says no suitable alternative could be identified.',
                    })}
                  </Grid>
                  <Grid item xs={12} sm={6}>{text('appeal_contact', 'Appeals to be sent to')}</Grid>
                </>
              )}
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{done ? 'Close' : 'Skip for now'}</Button>
        {!done && (
          <Button variant="contained" onClick={handleGenerate} disabled={saving || loading || !prefill}>
            {saving ? 'Creating...' : 'Create letter'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
